import calcPrice from 'common/utils/calc-price';
import formatCurrency from 'common/utils/format-currency';
import md5Array from 'common/utils/md5-array';
import { NOTIFICATION_TYPE } from 'components/v2/notification/notification';
import {
  DEFAULT_PARCEL_CATEGORY,
  PARCEL_CATEGORY,
} from 'constants/country-constants';
import { enableUIV2 } from 'helper/configuration';
import { getMexCreateDeliveryInfo, isFromMexApp } from 'helper/mex-app-helper';
import { enterpriseForQuoteParam, filterHardBlockErrors } from 'helper/quote';
import {
  getCompanyID,
  getUserCountryCode,
  getUserSafeID,
  isUsingPaxPersonalPayments,
} from 'helper/user';
import I18n from 'i18n-js';
import _clone from 'lodash/clone';
import _defaults from 'lodash/defaults';
import _flattenDeep from 'lodash/flattenDeep';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _includes from 'lodash/includes';
import _isEmpty from 'lodash/isEmpty';
import _isUndefined from 'lodash/isUndefined';
import _mapKeys from 'lodash/mapKeys';
import _mapValues from 'lodash/mapValues';
import _pickBy from 'lodash/pickBy';
import _startsWith from 'lodash/startsWith';
import _sum from 'lodash/sum';
import _values from 'lodash/values';
import { FIELD_NAME, PERSON_REQUIRED_FIELDS } from 'views/v2/home/constants';
import {
  BookingMode,
  CASH,
  CASH_BY_RECIPIENT,
  CASH_BY_SENDER,
} from '../constants/booking-constants';

import { getSenderStorage } from './storage';

export const validRecipientLocations = order => {
  if (_isEmpty(order.recipientLocations)) return [];
  return order.recipientLocations.filter(l => !l.deleted);
};

// rawPersonInfo parameter is raw formfield
const checkFormValuesInvalid = rawPersonInfo => {
  if (_isEmpty(rawPersonInfo)) return true;

  return Object.keys(rawPersonInfo).some(key => {
    const requiredField = _includes(PERSON_REQUIRED_FIELDS, key);

    if (!requiredField) return false;

    const field = rawPersonInfo[key];
    return _isEmpty(field.value) || !_isEmpty(field.errors);
  });
};

export const checkCODService = cashOnDelivery => !_isEmpty(cashOnDelivery);

export const checkCODServiceInQuote = order => {
  if (_isEmpty(order.quote)) return false;

  const serviceTypes = _get(order, 'serviceTypes', []);
  if (_isEmpty(serviceTypes)) return false;

  const selectedServiceType = serviceTypes.find(
    serviceType => serviceType.ID === _get(order, 'quote.forRender.serviceID'),
  );
  if (_isEmpty(selectedServiceType)) return false;

  return checkCODService(selectedServiceType.cashOnDelivery);
};

// For future use of setting COD value restrictions according to config
export const getServiceCODConfigInQuote = order => {
  if (_isEmpty(order.quote)) return {};

  const selectedServiceType = order.serviceTypes.find(
    serviceType => serviceType.ID === _get(order, 'quote.forRender.serviceID'),
  );
  if (_isEmpty(selectedServiceType)) return {};

  return selectedServiceType.cashOnDelivery;
};

export const getSenderInfo = order => {
  const rawSenderInfo = _mapKeys(
    _pickBy(order, (value, key) => key.includes('-sender')),
    (value, key) => key.replace('-sender', ''),
  );
  const senderInfo = _mapValues(rawSenderInfo, formField =>
    _get(formField, 'value'),
  );
  const isFormInvalid = checkFormValuesInvalid(rawSenderInfo);
  const location = _get(order, 'senderLocation', {});
  return {
    ...senderInfo,
    isFormInvalid,
    address: _get(location, 'address.combined_address'),
    keywords: _get(location, 'address.name'),
    coordinates: location.latlng,
    poiID: location.id,
    cityID: _get(location, 'address.cityId'),
    city: _get(location, 'address.city'),
  };
};

export const getRecipientInfo = order => {
  if (!order || !order.recipientLocations) return {};

  const recipientInfo = {};
  order.recipientLocations.forEach((location, index) => {
    if (location.deleted) return;
    const rawRecipientInfo = _mapKeys(
      _pickBy(order, (value, key) => key.includes(`-recipient-${index}`)),
      (value, key) => key.replace(`-recipient-${index}`, ''),
    );
    const isFormInvalid = checkFormValuesInvalid(rawRecipientInfo);
    recipientInfo[index] = {
      address: _get(location, 'address.combined_address'),
      keywords: _get(location, 'address.name'),
      coordinates: location.latlng,
      poiID: location.id,
      cityID: _get(location, 'address.cityId'),
      city: _get(location, 'address.city'),
      name: _get(order, `name-recipient-${index}.value`),
      cod: _get(order, `cod-recipient-${index}.value`),
      phone: _get(order, `phone-recipient-${index}.value`),
      note: _get(order, `note-recipient-${index}.value`),
      isFormInvalid,
    };
  });

  return recipientInfo;
};

export const getItemInfo = order => {
  const strCategoryID = _get(order, `${FIELD_NAME.ITEM_TYPE}.value`);
  const strWeight = _get(order, `${FIELD_NAME.ITEM_WEIGHT}.value`);
  const categoryID = isNaN(parseInt(strCategoryID, 10))
    ? null
    : parseInt(strCategoryID, 10);
  const weight = isNaN(parseFloat(strWeight)) ? null : parseFloat(strWeight);

  return {
    categoryID,
    categoryName: I18n.t(`v2.homepage.manualOrder.category.${categoryID}`, {
      defaultValue: '',
    }),
    weight,
  };
};

export const getPaidBy = order => {
  const paymentID = _get(order[FIELD_NAME.PAYMENT_METHOD], 'value');
  return paymentID === CASH_BY_SENDER || paymentID === CASH_BY_RECIPIENT
    ? paymentID
    : null;
};

export const getSelectedPayment = (order, payments) => {
  const paymentID =
    _get(order, `[${FIELD_NAME.PAYMENT_METHOD}].value`) || CASH_BY_SENDER;
  const payment =
    paymentID === CASH_BY_SENDER || paymentID === CASH_BY_RECIPIENT
      ? {
          paymentTypeID: '',
          type: CASH,
        }
      : payments.find(p => p.paymentTypeID === paymentID) || {};
  return payment;
};

export const getSelectedServiceType = order => {
  if (_isEmpty(order)) return;

  const serviceID = order[FIELD_NAME.SERVICE_TYPE].value; // must have, no need to check empty
  const serviceTypes = _get(order, 'serviceTypes', []);
  return serviceTypes.find(s => s.ID.toString() === serviceID);
};

export const getCreateOrderIDHash = (
  userInfo,
  sender,
  recipientMap,
  timeStampStr,
) => {
  let hashParams = [].concat(
    getCompanyID(userInfo) || '',
    getUserSafeID(userInfo) || '',
    sender.name,
    sender.phone,
    sender.address,
    Object.keys(recipientMap).map(key => {
      const recipient = recipientMap[key];
      return [recipient.name, recipient.phone, recipient.address];
    }),
  );
  if (!enableUIV2()) {
    /* in UI V1, createRequestID is used to check and show notification among multiple bookings
    * -> createRequestID must be different on each booking
    * Additionally, BE does not use createRequestID for anything
    */
    hashParams = hashParams.concat(timeStampStr || '');
  }
  return md5Array(_flattenDeep(hashParams));
};

export const getCreateOrderIDHashForFailedOrder = (userInfo, order) => {
  const sender = getSenderInfo(order);
  const recipientMap = getRecipientInfo(order);
  return getCreateOrderIDHash(userInfo, sender, recipientMap);
};

export const hasCODFieldValue = order => {
  const codFields = _pickBy(order, (_, key) =>
    _startsWith(key, 'cod-recipient'),
  );

  return _values(codFields).some(
    codField =>
      !_isEmpty(codField.value) &&
      _isEmpty(codField.errors) &&
      Number(codField.value) > 0,
  );
};

export const isValidCODFields = order => {
  const codFields = _pickBy(order, (_, key) =>
    _startsWith(key, 'cod-recipient'),
  );

  // Check hasCODFieldError
  const hasCODFieldError = _values(codFields).some(
    codField => !_isEmpty(codField.errors),
  );
  if (hasCODFieldError) return false;

  // Check hasCODFieldValue
  const hasCODFieldValue = _values(codFields).some(
    codField =>
      !_isEmpty(codField.value) &&
      _isEmpty(codField.errors) &&
      Number(codField.value) > 0,
  );
  if (!hasCODFieldValue) return true;

  // Check isCODService
  const isCODService = checkCODServiceInQuote(order);
  if (!isCODService) return false;

  return true;
};

export const getSelectedQuote = order => {
  const serviceID = order[FIELD_NAME.SERVICE_TYPE].value; // must have, no need to check empty;
  const quotes = _get(order, 'quote.deliveries[0].quotes', []) || [];
  return quotes.find(q => q.serviceID.toString() === serviceID);
};

const toPlaceOrderV2 = (orders, userInfo, payments) => {
  const result = {
    bookingMode: orders.length > 1 ? BookingMode.Multi : BookingMode.Single,
  };
  result.deliveries = orders.map(order => {
    const serviceType = getSelectedServiceType(order);
    const payment = getSelectedPayment(order, payments);
    const quote = getSelectedQuote(order);
    const sender = getSenderInfo(order);
    const recipientMap = getRecipientInfo(order);
    const itemInfo = getItemInfo(order);
    const recipientsPlaceOrder = Object.keys(recipientMap).map(key => {
      const recipient = recipientMap[key];

      return {
        contact: {
          name: recipient.name,
          phone: recipient.phone,
          instruction: recipient.note,
        },
        // first stop for pickup (job 0), rest for dropoff (job 1)
        jobType: 1,
        location: {
          address: recipient.address,
          keywords: recipient.keywords,
          coordinates: recipient.coordinates,
          poiID: recipient.poiID,
        },
        cashOnDelivery: !_isEmpty(recipient.cod)
          ? {
              enable: true,
              amount: Number(recipient.cod),
            }
          : null,
        itemInfo,
      };
    });

    return {
      createRequestID: getCreateOrderIDHash(userInfo, sender, recipientMap),
      discountMeta: quote.discountMeta,
      noteToDriver: _get(order[FIELD_NAME.INSTRUCTION], 'value', ''),
      paidBy: getPaidBy(order),
      paymentMethod: {
        id: payment.paymentTypeID,
        name: payment.refNumber,
        type: payment.type,
      },
      service: {
        expressServiceID: serviceType.expressServiceID,
        isPromoApplied: false,
        promoCode: quote.isDiscountApplied
          ? _get(order[FIELD_NAME.PROMO], 'value')
          : null,
        quoteSignature: quote.quoteSignature,
        serviceID: serviceType.ID,
        serviceName: serviceType.name,
      },
      steps: [
        {
          contact: {
            name: sender.name,
            phone: sender.phone,
            instruction: sender.note,
          },
          // first stop for pickup (job 0), rest for dropoff (job 1)
          jobType: 0,
          location: {
            address: sender.address,
            keywords: sender.keywords,
            coordinates: sender.coordinates,
            poiID: sender.poiID,
          },
        },
        ...recipientsPlaceOrder,
      ],
      countryCode: getUserCountryCode(userInfo),
      cityCode: sender.city, // TODO: need to pass city code, not city name
      referenceCode: _get(order, `${FIELD_NAME.REFERENCE_CODE}.value`, ''),
      ...(isFromMexApp() && getMexCreateDeliveryInfo()),
    };
  });
  if (!isUsingPaxPersonalPayments()) {
    result.enterprise = enterpriseForQuoteParam(userInfo);
  }

  return result;
};

export const toBookAgain = (queryOrder, pois) => {
  const steps = queryOrder.steps;
  const sender = {
    'name-sender': {
      value: _get(steps, '[0].contact.name'),
    },
    'phone-sender': {
      value: _get(steps, '[0].contact.phone'),
    },
    'note-sender': {
      value: _get(steps, '[0].contact.instruction'),
    },
  };

  const recipients = steps.slice(1).reduce(
    (acc, step, i) =>
      Object.assign({}, acc, {
        [`name-recipient-${i}`]: {
          value: _get(step, 'contact.name'),
        },
        [`phone-recipient-${i}`]: {
          value: _get(step, 'contact.phone'),
        },
        [`note-recipient-${i}`]: {
          value: _get(step, 'contact.instruction'),
        },
        [`cod-recipient-${i}`]: {
          value: Number(_get(step, 'cashOnDelivery.amount', '')) || undefined,
        },
      }),
    {},
  );
  const itemType = _get(queryOrder, 'steps[1].itemInfo.categoryID');

  const orderToBookAgain = {
    ...sender,
    ...recipients,
    senderLocation: pois[0],
    recipientLocations: pois.slice(1),
    [FIELD_NAME.INSTRUCTION]: {
      value: _get(queryOrder, 'noteToDriver', '').toString(),
    },
    [FIELD_NAME.SERVICE_TYPE]: {
      value: _get(queryOrder, 'service.serviceID', '').toString(),
    },
    [FIELD_NAME.PAYMENT_METHOD]: {
      value:
        _get(queryOrder, 'paymentMethod.id') ||
        _get(queryOrder, 'paymentMethod.paidBy') ||
        CASH_BY_SENDER,
    },
    [FIELD_NAME.ITEM_TYPE]: {
      value: itemType ? itemType.toString() : null,
    },
    [FIELD_NAME.ITEM_WEIGHT]: {
      value: _get(queryOrder, 'steps[1].itemInfo.weight'),
    },
  };

  return orderToBookAgain;
};

export const toRouteOptimisationRequest = (order, userInfo) => {
  let enterprise;
  const { currentCompany, currentGroup } = userInfo;
  if (currentCompany && currentCompany) {
    enterprise = {
      companyID: currentCompany.id,
      companyName: currentCompany.name,
      groupID: currentGroup.id,
      groupName: currentGroup.name,
    };
  }

  const itinerary = [
    order.senderLocation,
    ...validRecipientLocations(order),
  ].map(location => ({
    place: {
      id: location.id,
      details: {
        address: location.address.combined_address,
        keywords: location.address.name,
      },
      coordinate: location.latlng,
      cityID: location.address.cityId,
      countryID: location.address.country_id,
      countryCode: _get(userInfo, ['selectedCity', 'countryCode']),
    },
  }));

  return {
    input: {
      baseConfiguration: {
        itinerary,
      },
      configuration: {
        enterprise,
        EnableOptimizedRoute: true,
        taxiTypeID: +_get(order, ['SERVICE_TYPE', 'value'], 0),
      },
    },
  };
};

export const toGetQuote = (order, userInfo, payments) => {
  const serviceTypes = [];
  const deliveries = [];

  const itinerary = [
    order.senderLocation,
    ...validRecipientLocations(order),
  ].map(location => ({
    coordinates: location.latlng,
    address: location.address.combined_address,
    keywords: location.address.name,
    poiID: location.id,
  }));

  const payment = getSelectedPayment(order, payments);

  order.serviceTypes.forEach(service => {
    serviceTypes.push({
      serviceID: service.ID,
      expressServiceID: service.expressServiceID,
      serviceTypeName: service.name,
    });
    deliveries.push({
      quoteRequestID: md5Array(itinerary.map(i => i.address)),
      serviceID: service.ID,
      promoCode: _get(order[FIELD_NAME.PROMO], 'value', null),
      paymentMethodID: payment.paymentTypeID,
      paymentMethodType: payment.type,
      itinerary,
    });
  });
  const quote = {
    serviceTypes,
    deliveries,
  };
  if (!isUsingPaxPersonalPayments()) {
    quote.enterprise = enterpriseForQuoteParam(userInfo);
  }

  return quote;
};

export const allowToPlaceOrder = order => {
  if (
    order.fetchingQuote ||
    isNaN(_get(order[FIELD_NAME.ITEM_WEIGHT], 'value')) ||
    !_isEmpty(_get(order[FIELD_NAME.ITEM_WEIGHT], 'errors')) ||
    !_isEmpty(_get(order[FIELD_NAME.INSTRUCTION], 'errors')) ||
    !_isEmpty(_get(order[FIELD_NAME.REFERENCE_CODE], 'errors')) ||
    !_isEmpty(_get(order[FIELD_NAME.PROMO], 'errors')) ||
    _isEmpty(_get(order[FIELD_NAME.ITEM_TYPE], 'value')) ||
    _isEmpty(order.quote) ||
    _isEmpty(order.quote.forRender) ||
    !_isEmpty(filterHardBlockErrors(order.quote.forRender.errors))
  ) {
    return false;
  }

  const sender = getSenderInfo(order);
  if (sender.isFormInvalid) return false;

  if (!isValidCODFields(order)) return false;

  const recipients = getRecipientInfo(order);
  return !Object.keys(recipients).some(i => recipients[i].isFormInvalid);
};

export const toPlaceOrder = (orders, userInfo, payments) =>
  toPlaceOrderV2(orders, userInfo, payments);

const defaultExtraFields = index => ({
  [`name-recipient-${index}`]: { value: '' },
  [`phone-recipient-${index}`]: { value: '' },
  [`cod-recipient-${index}`]: { value: undefined },
  [`note-recipient-${index}`]: { value: '' },
});

const makeExtraFieldsNames = index => [
  `name-recipient-${index}`,
  `phone-recipient-${index}`,
  `cod-recipient-${index}`,
  `note-recipient-${index}`,
];

const copyFieldFrom = (order, oldFieldName, newFieldName) => ({
  ...order[oldFieldName],
  name: newFieldName,
});

export const moveExtraFields = (order, oldIndexes) => {
  const newOrder = { ...order };
  newOrder.recipientLocations.forEach((_, index) => {
    const oldIndex = oldIndexes[index];
    let extraFields;
    if (oldIndex === undefined) {
      extraFields = defaultExtraFields(index);
    } else {
      const oldFieldNames = makeExtraFieldsNames(oldIndex);
      extraFields = makeExtraFieldsNames(index).reduce(
        (fields, fieldName, i) => ({
          ...fields,
          [fieldName]: copyFieldFrom(order, oldFieldNames[i], fieldName),
        }),
        {},
      );
    }
    Object.assign(newOrder, extraFields);
  });
  return newOrder;
};

export const setExtraFields = order => {
  order.recipientLocations.forEach((_, index) => {
    _defaults(order, defaultExtraFields(index));
  });
};

// To be used when values come back from BE
export const getAmountDisplay = (amount, currency, hideSymbol = true) =>
  currency
    ? formatCurrency({
        code: currency.code,
        symbol: currency.symbol,
        amount: calcPrice(amount || 0, currency),
        hideSymbol,
      })
    : '';

// To be used when values entered by user
export const formatMajorUnitsForDisplay = (amountInMajor, currency) =>
  currency
    ? formatCurrency({
        code: currency.code,
        symbol: currency.symbol,
        amount: amountInMajor,
        hideSymbol: false,
      })
    : '';

export const getQuoteToRender = order => {
  if (_isEmpty(order.quote)) return {};

  const quote = getSelectedQuote(order);
  if (!quote) return {};

  // if quote amount is 0, the field is omitted in the response.
  // quote amount can be 0 due to promos or specially configured TTs
  if (!_has(quote, 'amount') && quote.quoteSignature) {
    quote.amount = 0;
  }

  const { amount, currency } = quote;
  return {
    ...quote,
    fetchingQuote: order.fetchingQuote,
    formattedAmount: getAmountDisplay(amount, currency, false),
    amountDisplay: getAmountDisplay(amount, currency, true),
  };
};

export const getQuotesToRender = orders =>
  orders.map(order => (_isEmpty(order) ? {} : getQuoteToRender(order)));

export const getSenderToStorage = order =>
  _pickBy(
    order,
    (field, key) =>
      key === 'senderLocation' ||
      (key.includes('-sender') && !_isUndefined(_get(field, 'value'))),
  );

export const checkBookingFormReloadable = (userInfo, orders) => {
  if (_isEmpty(orders)) return true;

  const cityID = _get(userInfo, 'selectedCity.cityID');
  const userSafeID = _get(userInfo, 'user.safeID');

  const singleOrder = orders[0];
  const bulkOrders = orders.slice(1);

  const singleOrderReloadable =
    _isEmpty(singleOrder.recipientLocations) &&
    (_isEmpty(singleOrder.senderLocation) ||
      JSON.stringify(getSenderToStorage(singleOrder)) ===
        JSON.stringify(getSenderStorage(cityID, userSafeID)));
  const bulkOrdersReloadable = bulkOrders.every(
    order =>
      _isEmpty(order.senderLocation) && _isEmpty(order.recipientLocations),
  );

  return singleOrderReloadable && bulkOrdersReloadable;
};

export const getInitialReduxOrder = serviceTypes => ({
  serviceTypes,
  [FIELD_NAME.SERVICE_TYPE]: {
    name: FIELD_NAME.SERVICE_TYPE,
    value: _get(serviceTypes, '[0].ID', '').toString(),
  },
});

export const isShopifyOrder = thirdPartySource =>
  thirdPartySource === 'shopify';

export const generateLocalisedItemOptions = ({
  userInfo,
  pushNotification,
}) => {
  const countryCode = getUserCountryCode(userInfo);
  let IDs;
  IDs = PARCEL_CATEGORY[countryCode];
  if (!IDs) {
    if (pushNotification) {
      pushNotification({
        // constant ID so that multiple notifications don't show up on multiple booking page.
        id: '1',
        message: I18n.t('v2.error.booking.parcelConfigMissing.message'),
        description: I18n.t('v2.error.booking.parcelConfigMissing.description'),
        type: NOTIFICATION_TYPE.WARNING,
      });
    }
    IDs = DEFAULT_PARCEL_CATEGORY;
  }
  return IDs.map(id => {
    const iString = I18n.t(`v2.homepage.manualOrder.category.${id}`);
    return {
      id,
      iString,
    };
  });
};

export const countTotalOrders = deliveries => {
  if (!_isEmpty(deliveries)) {
    return deliveries.reduce(
      (total, delivery) => total + _get(delivery, ['steps', 'length'], 0) - 1,
      0,
    );
  }
  return 0;
};

export const getDeliveryID = o => {
  if (!o) {
    return undefined;
  }
  if (o.deliveryID) {
    return o.deliveryID;
  }
  const stepsLen = _get(o, 'steps.length', 0);
  if (stepsLen > 2) {
    return o.bookingCode;
  }
  return o.orderID;
};

export const isMultiStopDelivery = o => {
  if (!o) {
    return false;
  }
  return _get(o, ['steps', 'length'], 0) > 2;
};

const emptyInfo = {
  name: '--',
  phone: '--',
  keywords: '--',
};

const stepToInfo = step => ({
  name: _get(step, 'contact.name', '--'),
  phone: _get(step, 'contact.phone', '--'),
  keywords: _get(step, 'location.address', '--'),
});

export const getSenderAndRecipientInfo = order => {
  if (!order || !order.steps || order.steps.length < 2) {
    return {
      sender: _clone(emptyInfo),
      recipients: [_clone(emptyInfo)],
    };
  }

  return {
    sender: stepToInfo(order.steps[0]),
    recipients: order.steps.slice(1).map(stepToInfo),
  };
};

export const getOrderItemWeight = order => {
  const steps = _get(order, 'steps');
  return !_isEmpty(steps)
    ? _sum(steps.slice(1).map(step => _get(step, ['itemInfo', 'weight'], 0)))
    : '--';
};
